import { useTranslation } from '@/i18n';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { ExportDropdown } from '@/components/ui/filters/ExportDropdown';
import { Table } from '@/components/ui/tables/Table';
import { ListPanel } from '@/components/ui/panels';
import type { RoleSection, SystemPage } from '@/api/domains/authorization/rolePermissions.types';
import { usePermissions } from '@/hooks/authorization/usePermissions';


export function PermissionsForm() {
  const { t: translate } = useTranslation();
  const {
    pages,
    isPagesLoading,
    selectedPageId,
    pagesSearchText,
    sentinelRef,
    scrollContainerRef,
    onPageSelect,
    onPagesSearch,
    roleOptions,
    selectedRoleId,
    onRoleChange,
    filteredSections,
    isSectionsLoading,
    columns,
    isDirty,
    isSaving,
    onSave,
    onCancel,
    onExport,
    isExporting,
  } = usePermissions();

  return (
    <div className="flex flex-col gap-4" data-testid="permissions-form">
      {/* Split panel: páginas (izq) + secciones (der) */}
      <div className="flex gap-20 items-start">
        <ListPanel<SystemPage>
          items={pages}
          renderItem={(page) => (
            <ListPanel.Item
              title={page.name}
              subtitle={page.url}
              isSelected={selectedPageId === page.id}
              onClick={() => onPageSelect(page.id)}
              data-testid={`page-item-${page.id}`}
            />
          )}
          keyExtractor={(page) => page.id}
          isLoading={isPagesLoading}
          searchValue={pagesSearchText}
          onSearch={onPagesSearch}
          searchPlaceholder={translate('authorization:permissions.searchPages')}
          title={translate('authorization:permissions.pages')}
          sentinelRef={sentinelRef}
          scrollContainerRef={scrollContainerRef}
          data-testid="pages-panel"
        />

        <div className="flex-1 min-w-0 flex flex-col gap-8">
          {/* Selector de rol + botón descargar */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-96">
              <Selector
                label={translate('authorization:permissions.role')}
                options={roleOptions}
                value={selectedRoleId ? String(selectedRoleId) : undefined}
                onChange={(value) => onRoleChange(value ? Number(value) : null)}
                data-testid="role-selector"
              />
            </div>

            <div className="ml-auto">
              <ExportDropdown
                onExport={() => { void onExport(); }}
                availableFormats={['EXCEL']}
                disabled={!selectedRoleId || isExporting}
                isLoading={isExporting}
                data-testid="download-button"
              />
            </div>
          </div>

          <Table<RoleSection>
            columns={columns}
            data={filteredSections}
            totalRows={filteredSections.length}
            isLoading={isSectionsLoading}
            searchable
            searchPlaceholder={translate('authorization:permissions.searchContent')}
            className="flex-1 min-w-0"
            data-testid="sections-table"
          />

          {/* Acciones */}
          <div className="flex justify-center gap-3 pt-2">
            <PrimaryButton
              onClick={onCancel}
              variant="ghost"
              disabled={isSaving}
              data-testid="cancel-button"
            >
              {translate('common:actions.cancel')}
            </PrimaryButton>

            <PrimaryButton
              onClick={() => { void onSave(); }}
              disabled={!isDirty || isSaving}
              data-testid="save-button"
            >
              {isSaving
                ? translate('common:actions.saving')
                : translate('authorization:permissions.saveChanges')}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PermissionsForm;
